import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronLeft, TrendingUp, Lightbulb, AlertTriangle, Pill, Flame, BarChart3, ArrowUp, ArrowDown, Minus, Moon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CorrelationInsights = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [supplements, setSupplements] = useState([]);
  const [flares, setFlares] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [sleepInsights, setSleepInsights] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [supplementsRes, flaresRes, sleepRes] = await Promise.all([
        fetch(`${API_URL}/api/supplements`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/flare-journal`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/sleep?days=60`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const suppsData = await supplementsRes.json();
      const flaresData = await flaresRes.json();
      const sleepEntries = await sleepRes.json();

      const supps = suppsData.supplements || [];
      const flareEntries = Array.isArray(flaresData) ? flaresData : (flaresData.entries || []);

      setSupplements(supps);
      setFlares(flareEntries);
      setSleepData(sleepEntries);
      
      if (flareEntries.length >= 3) {
        analyzeCorrelations(supps, flareEntries);
      }
      
      if (sleepEntries.length >= 3 && flareEntries.length >= 3) {
        analyzeSleepCorrelations(sleepEntries, flareEntries);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeSleepCorrelations = (sleepEntries, flareEntries) => {
    const results = {
      avgHoursOnFlare: null,
      avgHoursNoFlare: null,
      avgQualityOnFlare: null,
      avgQualityNoFlare: null,
      poorSleepFlareRate: null,
      goodSleepFlareRate: null,
      issueCorrelations: []
    };

    // Create date-based lookup for flares
    const flareDates = new Set(flareEntries.map(f => f.date));
    
    // Separate sleep entries by whether next day had a flare
    const sleepBeforeFlare = [];
    const sleepNoFlare = [];
    
    sleepEntries.forEach(sleep => {
      const sleepDate = new Date(sleep.date);
      const nextDay = new Date(sleepDate);
      nextDay.setDate(nextDay.getDate() + 1);
      const nextDayStr = nextDay.toISOString().split('T')[0];
      
      if (flareDates.has(nextDayStr)) {
        sleepBeforeFlare.push(sleep);
      } else {
        sleepNoFlare.push(sleep);
      }
    });

    // Calculate averages
    if (sleepBeforeFlare.length > 0) {
      const hoursFlare = sleepBeforeFlare.filter(s => s.total_hours).map(s => s.total_hours);
      const qualityFlare = sleepBeforeFlare.filter(s => s.sleep_quality).map(s => s.sleep_quality);
      results.avgHoursOnFlare = hoursFlare.length > 0 ? (hoursFlare.reduce((a, b) => a + b, 0) / hoursFlare.length).toFixed(1) : null;
      results.avgQualityOnFlare = qualityFlare.length > 0 ? (qualityFlare.reduce((a, b) => a + b, 0) / qualityFlare.length).toFixed(1) : null;
    }

    if (sleepNoFlare.length > 0) {
      const hoursNoFlare = sleepNoFlare.filter(s => s.total_hours).map(s => s.total_hours);
      const qualityNoFlare = sleepNoFlare.filter(s => s.sleep_quality).map(s => s.sleep_quality);
      results.avgHoursNoFlare = hoursNoFlare.length > 0 ? (hoursNoFlare.reduce((a, b) => a + b, 0) / hoursNoFlare.length).toFixed(1) : null;
      results.avgQualityNoFlare = qualityNoFlare.length > 0 ? (qualityNoFlare.reduce((a, b) => a + b, 0) / qualityNoFlare.length).toFixed(1) : null;
    }

    // Calculate flare rates by sleep quality
    const poorSleepNights = sleepEntries.filter(s => s.sleep_quality && s.sleep_quality <= 2);
    const goodSleepNights = sleepEntries.filter(s => s.sleep_quality && s.sleep_quality >= 4);
    
    if (poorSleepNights.length > 0) {
      const poorSleepFlares = poorSleepNights.filter(s => {
        const nextDay = new Date(s.date);
        nextDay.setDate(nextDay.getDate() + 1);
        return flareDates.has(nextDay.toISOString().split('T')[0]);
      });
      results.poorSleepFlareRate = Math.round((poorSleepFlares.length / poorSleepNights.length) * 100);
    }

    if (goodSleepNights.length > 0) {
      const goodSleepFlares = goodSleepNights.filter(s => {
        const nextDay = new Date(s.date);
        nextDay.setDate(nextDay.getDate() + 1);
        return flareDates.has(nextDay.toISOString().split('T')[0]);
      });
      results.goodSleepFlareRate = Math.round((goodSleepFlares.length / goodSleepNights.length) * 100);
    }

    // Analyze sleep issues correlations
    const issues = ['nightmares', 'bedwetting', 'sleep_anxiety', 'insomnia'];
    issues.forEach(issue => {
      const nightsWithIssue = sleepEntries.filter(s => s[issue]);
      if (nightsWithIssue.length >= 2) {
        const issueFlares = nightsWithIssue.filter(s => {
          const nextDay = new Date(s.date);
          nextDay.setDate(nextDay.getDate() + 1);
          return flareDates.has(nextDay.toISOString().split('T')[0]);
        });
        const flareRate = Math.round((issueFlares.length / nightsWithIssue.length) * 100);
        results.issueCorrelations.push({
          issue: issue.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          nights: nightsWithIssue.length,
          flareRate
        });
      }
    });

    results.issueCorrelations.sort((a, b) => b.flareRate - a.flareRate);
    setSleepInsights(results);
  };

  const analyzeCorrelations = (supps, flareEntries) => {
    const analysisResults = {
      triggerPatterns: [],
      supplementEffectiveness: [],
      severityTrends: {},
      timePatterns: {},
      recommendations: []
    };

    // Analyze trigger patterns
    const triggerCounts = {};
    const triggerSeverity = {};
    flareEntries.forEach(flare => {
      const triggers = [...(flare.triggers || []), ...(flare.symptoms || [])];
      triggers.forEach(trigger => {
        triggerCounts[trigger] = (triggerCounts[trigger] || 0) + 1;
        if (!triggerSeverity[trigger]) triggerSeverity[trigger] = [];
        triggerSeverity[trigger].push(flare.severity || 5);
      });
    });

    // Sort triggers by frequency
    analysisResults.triggerPatterns = Object.entries(triggerCounts)
      .map(([trigger, count]) => ({
        trigger,
        count,
        percentage: Math.round((count / flareEntries.length) * 100),
        avgSeverity: triggerSeverity[trigger] 
          ? (triggerSeverity[trigger].reduce((a, b) => a + b, 0) / triggerSeverity[trigger].length).toFixed(1)
          : 'N/A'
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Analyze supplement effectiveness
    supps.filter(s => s.status === 'active').forEach(supp => {
      const startDate = new Date(supp.start_date);
      const flaresBefore = flareEntries.filter(f => new Date(f.date) < startDate);
      const flaresAfter = flareEntries.filter(f => new Date(f.date) >= startDate);

      if (flaresBefore.length >= 2 && flaresAfter.length >= 2) {
        const avgSeverityBefore = flaresBefore.reduce((sum, f) => sum + (f.severity || 5), 0) / flaresBefore.length;
        const avgSeverityAfter = flaresAfter.reduce((sum, f) => sum + (f.severity || 5), 0) / flaresAfter.length;
        const change = avgSeverityBefore - avgSeverityAfter;
        
        analysisResults.supplementEffectiveness.push({
          name: supp.name,
          type: supp.type,
          avgSeverityBefore: avgSeverityBefore.toFixed(1),
          avgSeverityAfter: avgSeverityAfter.toFixed(1),
          change: change.toFixed(1),
          trend: change > 0.5 ? 'improved' : change < -0.5 ? 'worsened' : 'stable',
          flaresBefore: flaresBefore.length,
          flaresAfter: flaresAfter.length
        });
      }
    });

    // Sort by improvement
    analysisResults.supplementEffectiveness.sort((a, b) => parseFloat(b.change) - parseFloat(a.change));

    // Analyze severity trends over time
    const sortedFlares = [...flareEntries].sort((a, b) => new Date(a.date) - new Date(b.date));
    if (sortedFlares.length >= 4) {
      const half = Math.floor(sortedFlares.length / 2);
      const firstHalf = sortedFlares.slice(0, half);
      const secondHalf = sortedFlares.slice(half);
      
      const avgFirst = firstHalf.reduce((sum, f) => sum + (f.severity || 5), 0) / firstHalf.length;
      const avgSecond = secondHalf.reduce((sum, f) => sum + (f.severity || 5), 0) / secondHalf.length;
      
      analysisResults.severityTrends = {
        firstPeriodAvg: avgFirst.toFixed(1),
        secondPeriodAvg: avgSecond.toFixed(1),
        change: (avgFirst - avgSecond).toFixed(1),
        trend: avgSecond < avgFirst ? 'improving' : avgSecond > avgFirst ? 'worsening' : 'stable'
      };
    }

    // Analyze day of week patterns
    const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    flareEntries.forEach(flare => {
      const day = new Date(flare.date).getDay();
      dayOfWeekCounts[day]++;
    });
    
    const maxDay = dayOfWeekCounts.indexOf(Math.max(...dayOfWeekCounts));
    const minDay = dayOfWeekCounts.indexOf(Math.min(...dayOfWeekCounts));
    
    analysisResults.timePatterns = {
      mostCommonDay: dayNames[maxDay],
      mostCommonCount: dayOfWeekCounts[maxDay],
      leastCommonDay: dayNames[minDay],
      leastCommonCount: dayOfWeekCounts[minDay],
      dayDistribution: dayNames.map((name, i) => ({ day: name, count: dayOfWeekCounts[i] }))
    };

    // Generate recommendations
    if (analysisResults.triggerPatterns.length > 0) {
      const topTrigger = analysisResults.triggerPatterns[0];
      analysisResults.recommendations.push({
        type: 'trigger',
        title: `Top Trigger: ${topTrigger.trigger}`,
        description: `Appeared in ${topTrigger.percentage}% of flare entries with average severity of ${topTrigger.avgSeverity}/10. Consider tracking exposure to this trigger more closely.`
      });
    }

    const effectiveSupps = analysisResults.supplementEffectiveness.filter(s => parseFloat(s.change) > 1);
    if (effectiveSupps.length > 0) {
      analysisResults.recommendations.push({
        type: 'supplement',
        title: `Potentially Helpful: ${effectiveSupps.map(s => s.name).join(', ')}`,
        description: `Flare severity decreased after starting ${effectiveSupps.length > 1 ? 'these' : 'this'}. Continue monitoring.`
      });
    }

    if (analysisResults.severityTrends.trend === 'improving') {
      analysisResults.recommendations.push({
        type: 'positive',
        title: 'Overall Improvement Detected',
        description: `Average flare severity has decreased from ${analysisResults.severityTrends.firstPeriodAvg} to ${analysisResults.severityTrends.secondPeriodAvg}. Your current approach appears to be working!`
      });
    }

    setInsights(analysisResults);
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improved' || trend === 'improving') return <ArrowDown className="w-4 h-4 text-green-600" />;
    if (trend === 'worsened' || trend === 'worsening') return <ArrowUp className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-stone-400" />;
  };

  const getTrendColor = (trend) => {
    if (trend === 'improved' || trend === 'improving') return 'text-green-600 bg-green-50';
    if (trend === 'worsened' || trend === 'worsening') return 'text-red-600 bg-red-50';
    return 'text-stone-600 bg-stone-50';
  };

  return (
    <div className={`${embedded ? '' : 'min-h-screen bg-gradient-to-br from-stone-50 to-purple-50'} pb-20`}>
      {!embedded && (
        <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-nunito font-bold text-[#2D2D2D]">Correlation Insights</h1>
                <p className="text-sm text-[#57534E]">Discover patterns in your health data</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`${embedded ? '' : 'max-w-4xl mx-auto px-4 py-6'} space-y-6`}>
        {loading ? (
          <Card className="bg-white rounded-xl p-8 text-center">
            <p className="text-[#57534E]">Analyzing your data...</p>
          </Card>
        ) : flares.length < 3 ? (
          <Card className="bg-white rounded-xl p-8 text-center">
            <BarChart3 className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Not Enough Data Yet</h3>
            <p className="text-sm text-[#57534E] mb-4">
              Log at least 3 flare entries to start seeing correlation insights. 
              The more data you track, the better the insights!
            </p>
            <Button onClick={() => navigate('/flare-journal')} className="bg-accent hover:bg-accent/90">
              <Flame className="w-4 h-4 mr-2" /> Go to Flare Journal
            </Button>
          </Card>
        ) : (
          <>
            {/* Quick Summary Card for Doctor Visits */}
            <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border-emerald-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                <h2 className="font-nunito font-bold text-emerald-800">Quick Summary for Doctor Visits</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Top 3 Triggers */}
                <div className="bg-white/70 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Top 3 Triggers
                  </h3>
                  {insights?.triggerPatterns?.slice(0, 3).length > 0 ? (
                    <ol className="space-y-2">
                      {insights.triggerPatterns.slice(0, 3).map((trigger, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                          <span className="text-[#2D2D2D] font-medium">{trigger.trigger}</span>
                          <span className="text-[#57534E]">({trigger.percentage}%)</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-[#57534E] italic">Not enough data yet</p>
                  )}
                </div>
                
                {/* Top 3 Effective Supplements */}
                <div className="bg-white/70 rounded-lg p-4">
                  <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <Pill className="w-4 h-4" />
                    Most Effective Supplements
                  </h3>
                  {insights?.supplementEffectiveness?.filter(s => parseFloat(s.change) > 0).slice(0, 3).length > 0 ? (
                    <ol className="space-y-2">
                      {insights.supplementEffectiveness.filter(s => parseFloat(s.change) > 0).slice(0, 3).map((supp, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                          <span className="text-[#2D2D2D] font-medium">{supp.name}</span>
                          <span className="text-green-600">(↓{supp.change} severity)</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-[#57534E] italic">Not enough data yet</p>
                  )}
                </div>
              </div>
              <p className="text-xs text-emerald-700 mt-3 text-center">
                💡 Screenshot this card to share with your healthcare provider
              </p>
            </Card>

            {/* Recommendations */}
            {insights?.recommendations?.length > 0 && (
              <Card className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border-purple-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-5 h-5 text-purple-600" />
                  <h2 className="font-nunito font-bold text-purple-800">Key Insights</h2>
                </div>
                <div className="space-y-3">
                  {insights.recommendations.map((rec, i) => (
                    <div key={i} className="bg-white/70 rounded-lg p-3">
                      <p className="font-semibold text-[#2D2D2D]">{rec.title}</p>
                      <p className="text-sm text-[#57534E]">{rec.description}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Severity Trend */}
            {insights?.severityTrends?.trend && (
              <Card className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-accent" />
                  <h2 className="font-nunito font-bold text-[#2D2D2D]">Overall Severity Trend</h2>
                </div>
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-stone-600">{insights.severityTrends.firstPeriodAvg}</p>
                    <p className="text-xs text-[#A8A29E]">Earlier Period Avg</p>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${getTrendColor(insights.severityTrends.trend)}`}>
                    {getTrendIcon(insights.severityTrends.trend)}
                    <span className="font-semibold capitalize">{insights.severityTrends.trend}</span>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-accent">{insights.severityTrends.secondPeriodAvg}</p>
                    <p className="text-xs text-[#A8A29E]">Recent Period Avg</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Top Triggers */}
            {insights?.triggerPatterns?.length > 0 && (
              <Card className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  <h2 className="font-nunito font-bold text-[#2D2D2D]">Most Common Triggers & Symptoms</h2>
                </div>
                <div className="space-y-2">
                  {insights.triggerPatterns.map((trigger, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-bold text-sm">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-[#2D2D2D]">{trigger.trigger}</span>
                          <span className="text-sm text-[#57534E]">{trigger.percentage}% of flares</span>
                        </div>
                        <div className="w-full bg-stone-100 rounded-full h-2 mt-1">
                          <div 
                            className="bg-orange-400 h-2 rounded-full" 
                            style={{ width: `${trigger.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-[#57534E]">Avg: {trigger.avgSeverity}/10</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Supplement Effectiveness */}
            {insights?.supplementEffectiveness?.length > 0 && (
              <Card className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Pill className="w-5 h-5 text-purple-600" />
                  <h2 className="font-nunito font-bold text-[#2D2D2D]">Supplement/Medication Impact</h2>
                </div>
                <p className="text-sm text-[#57534E] mb-4">
                  Comparing average flare severity before vs. after starting each supplement/medication.
                </p>
                <div className="space-y-3">
                  {insights.supplementEffectiveness.map((supp, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${
                      supp.trend === 'improved' ? 'bg-green-50 border-green-200' :
                      supp.trend === 'worsened' ? 'bg-red-50 border-red-200' : 'bg-stone-50 border-stone-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getTrendIcon(supp.trend)}
                          <span className="font-semibold text-[#2D2D2D]">{supp.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            supp.type === 'medication' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                          }`}>{supp.type}</span>
                        </div>
                        <div className="text-right">
                          <span className={`font-bold ${
                            supp.trend === 'improved' ? 'text-green-600' :
                            supp.trend === 'worsened' ? 'text-red-600' : 'text-stone-600'
                          }`}>
                            {parseFloat(supp.change) > 0 ? '-' : '+'}{Math.abs(parseFloat(supp.change))} severity
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-4 mt-2 text-xs text-[#57534E]">
                        <span>Before: {supp.avgSeverityBefore}/10 ({supp.flaresBefore} flares)</span>
                        <span>→</span>
                        <span>After: {supp.avgSeverityAfter}/10 ({supp.flaresAfter} flares)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Day of Week Pattern */}
            {insights?.timePatterns?.dayDistribution && (
              <Card className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <h2 className="font-nunito font-bold text-[#2D2D2D]">Day of Week Patterns</h2>
                </div>
                <div className="flex items-end justify-between gap-2 h-32">
                  {insights.timePatterns.dayDistribution.map((day, i) => {
                    const maxCount = Math.max(...insights.timePatterns.dayDistribution.map(d => d.count));
                    const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <span className="text-xs font-semibold text-[#57534E] mb-1">{day.count}</span>
                        <div 
                          className="w-full bg-blue-400 rounded-t transition-all"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        ></div>
                        <span className="text-xs text-[#A8A29E] mt-1">{day.day.slice(0, 3)}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm text-[#57534E] mt-4 text-center">
                  Most flares occur on <strong>{insights.timePatterns.mostCommonDay}</strong> ({insights.timePatterns.mostCommonCount} flares)
                </p>
              </Card>
            )}

            {/* Sleep Correlations */}
            {sleepInsights && (sleepInsights.avgHoursOnFlare || sleepInsights.issueCorrelations.length > 0) && (
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border-indigo-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Moon className="w-5 h-5 text-indigo-600" />
                  <h2 className="font-nunito font-bold text-indigo-800">Sleep & Flare Correlations</h2>
                </div>
                
                {/* Sleep Hours/Quality Comparison */}
                {(sleepInsights.avgHoursOnFlare && sleepInsights.avgHoursNoFlare) && (
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-white/70 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-[#57534E] mb-2">Avg Sleep Before Flare Days</h3>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-red-600">{sleepInsights.avgHoursOnFlare}h</span>
                        {sleepInsights.avgQualityOnFlare && (
                          <span className="text-sm text-[#57534E]">Quality: {sleepInsights.avgQualityOnFlare}/5</span>
                        )}
                      </div>
                    </div>
                    <div className="bg-white/70 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-[#57534E] mb-2">Avg Sleep Before Good Days</h3>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-green-600">{sleepInsights.avgHoursNoFlare}h</span>
                        {sleepInsights.avgQualityNoFlare && (
                          <span className="text-sm text-[#57534E]">Quality: {sleepInsights.avgQualityNoFlare}/5</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Flare Rate by Sleep Quality */}
                {(sleepInsights.poorSleepFlareRate !== null && sleepInsights.goodSleepFlareRate !== null) && (
                  <div className="bg-white/70 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-[#57534E] mb-3">Flare Rate by Sleep Quality</h3>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Poor Sleep (1-2 quality)</span>
                          <span className="font-semibold text-red-600">{sleepInsights.poorSleepFlareRate}% flare rate</span>
                        </div>
                        <div className="w-full bg-stone-200 rounded-full h-2">
                          <div className="bg-red-500 h-2 rounded-full" style={{ width: `${sleepInsights.poorSleepFlareRate}%` }}></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Good Sleep (4-5 quality)</span>
                          <span className="font-semibold text-green-600">{sleepInsights.goodSleepFlareRate}% flare rate</span>
                        </div>
                        <div className="w-full bg-stone-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${sleepInsights.goodSleepFlareRate}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sleep Issues Correlations */}
                {sleepInsights.issueCorrelations.length > 0 && (
                  <div className="bg-white/70 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-[#57534E] mb-3">Sleep Issues → Next Day Flare Rate</h3>
                    <div className="space-y-2">
                      {sleepInsights.issueCorrelations.map((issue, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm text-[#2D2D2D]">{issue.issue}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#57534E]">({issue.nights} nights)</span>
                            <span className={`text-sm font-semibold ${issue.flareRate > 50 ? 'text-red-600' : issue.flareRate > 30 ? 'text-orange-600' : 'text-green-600'}`}>
                              {issue.flareRate}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-indigo-700 mt-3 text-center">
                  💡 Track more sleep data for better insights
                </p>
              </Card>
            )}

            {/* Disclaimer */}
            <Card className="bg-yellow-50 border-yellow-200 rounded-xl p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> These insights are based on your tracked data and are for informational purposes only. 
                Correlation does not imply causation. Always consult with your healthcare provider before making treatment decisions.
              </p>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default CorrelationInsights;
